import { zodResolver } from '@hookform/resolvers/zod';
import { t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { KeyMemberSchema as KeyMemberSchemaShared } from '@repo/shared/schema';
import { ReportAutofillStatus } from '@repo/shared/types';
import { Button } from '@repo/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/card';
import { DateInput } from '@repo/ui/date-input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { cn } from '@repo/ui/lib/utils';
import { Separator } from '@repo/ui/separator';
import { TypewriterField } from '@repo/ui/typewriter-input';
import { ArrowRight, Check, Loader2, Plus, Trash2, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useParams, useRouteLoaderData } from 'react-router';
import { z } from 'zod';
import type { WizardLoaderData, WizardReport } from '@/lib/report';
import { trpc } from '@/providers/trpc';
import { useSetUnsavedChanges } from '@/providers/unsaved-changes';

// _id is optional in the form: new members have no _id yet; existing members carry their DB id.
const KeyMemberFormSchema = KeyMemberSchemaShared.omit({ signals: true }).extend({
  _id: z.string().optional(),
});

const KeyMembersFormSchema = z.object({
  keyMembers: z.array(KeyMemberFormSchema).min(1, t('extra.addAtLeastOneKeyMember')),
});

type KeyMembersForm = z.infer<typeof KeyMembersFormSchema>;

type SaveState = 'idle' | 'saving' | 'saved';

// _id is intentionally omitted for new members so Mongoose generates a valid ObjectId
// on the server side. Sending _id: '' fails ObjectIdSchema validation and silently drops the save.
const EMPTY_MEMBER: z.infer<typeof KeyMemberFormSchema> = {
  memberRole: '',
  firstName: '',
  lastName: '',
  dateOfBirth: null,
  placeOfBirth: null,
  nationality: null,
  countryOfResidence: null,
  taxIdentificationNumber: null,
  idNumber: null,
  phoneNumber: null,
  email: null,
  primarySourceOfFundsIncome: null,
  estimatedNetWorth: null,
};

type KeyMembersStepProps = {
  onContinue: () => void;
};

function getFullName(firstName: string | undefined, lastName: string | undefined, index: number) {
  const fullName = `${firstName ? `${firstName}` : ''}${lastName ? ` ${lastName}` : ''}`;
  return fullName && fullName !== '' ? fullName : t('count.member', { count: index + 1 });
}

export function KeyMembersStep({ onContinue }: KeyMembersStepProps) {
  useLocale();

  const { reportId = '' } = useParams();

  const { report: initialReport } = useRouteLoaderData('report-create-wizard') as WizardLoaderData;

  const { setUnsavedChanges } = useSetUnsavedChanges();
  const hasMarkedUnsaved = useRef(false);

  const { data: report } = trpc.report.getReport.useQuery(
    { reportId },
    {
      initialData: initialReport as never,
      refetchInterval: (query) => {
        const data = query.state.data as WizardReport | undefined;
        return data?.autofillStatus === ReportAutofillStatus.PROCESSING ? 2500 : false;
      },
    },
  );

  const isAutofillRunning = report?.autofillStatus === ReportAutofillStatus.PROCESSING;

  const { mutateAsync: updateKeyMembers } = trpc.report.updateKeyMembers.useMutation({
    meta: { doNotInvalidateQueryOnMutation: true },
  });

  const initialMembers = (report?.keyMembers ?? []).map((m) => ({
    _id: (m as { _id?: string })?._id,
    memberRole: (m as { memberRole?: string }).memberRole ?? '',
    firstName: (m as { firstName?: string }).firstName ?? '',
    lastName: (m as { lastName?: string }).lastName ?? '',
    dateOfBirth: (m as { dateOfBirth?: string }).dateOfBirth ?? '',
    placeOfBirth: (m as { placeOfBirth?: string }).placeOfBirth ?? '',
    nationality: (m as { nationality?: string }).nationality ?? '',
    countryOfResidence: (m as { countryOfResidence?: string }).countryOfResidence ?? '',
    taxIdentificationNumber:
      (m as { taxIdentificationNumber?: string }).taxIdentificationNumber ?? '',
    idNumber: (m as { idNumber?: string }).idNumber ?? '',
    phoneNumber: (m as { phoneNumber?: string }).phoneNumber ?? '',
    email: (m as { email?: string }).email ?? '',
    primarySourceOfFundsIncome:
      (m as { primarySourceOfFundsIncome?: string }).primarySourceOfFundsIncome ?? '',
    estimatedNetWorth: (m as { estimatedNetWorth?: string }).estimatedNetWorth ?? '',
  }));

  const form = useForm<KeyMembersForm>({
    resolver: zodResolver(KeyMembersFormSchema, { error: validationError }),
    defaultValues: {
      keyMembers: initialMembers.length > 0 ? initialMembers : [EMPTY_MEMBER],
    },
  });

  const { isDirty } = form.formState;

  useEffect(() => {
    if (!isDirty || hasMarkedUnsaved.current) return;
    hasMarkedUnsaved.current = true;
    setUnsavedChanges({
      get message() {
        return t('reports.youHaveEditedKeyMemberDetailsLeavingNowMay');
      },
      get proceedLinkLabel() {
        return t('extra.leaveAnyway');
      },
    });
  }, [isDirty, setUnsavedChanges]);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'keyMembers',
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (selectedIndex >= fields.length) {
      setSelectedIndex(Math.max(0, fields.length - 1));
    }
  }, [fields.length, selectedIndex]);

  // Reset when autofill finishes
  useEffect(() => {
    if (!isAutofillRunning && (report?.keyMembers ?? []).length > 0) {
      const members = (report!.keyMembers ?? []).map((m) => ({
        ...m,
      }));
      form.reset({ keyMembers: members });
      setSelectedIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutofillRunning]);

  // ── Autosave ──────────────────────────────────────────────────────────────
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);
  const watchedValues = useWatch({ control: form.control });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(async () => {
      setSaveState('saving');
      try {
        await updateKeyMembers({
          reportId,
          keyMembers: (watchedValues.keyMembers ?? []).map((m) => ({
            ...m,
            _id: m._id,
          })),
        });
        setSaveState('saved');
        setTimeout(() => setSaveState('idle'), 2000);
      } catch {
        setSaveState('idle');
      }
    }, 600);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedValues]);

  const watchedMembers = useWatch({ control: form.control, name: 'keyMembers' });

  return (
    <Form {...form}>
      <form className="flex flex-col gap-6" onSubmit={(e) => e.preventDefault()}>
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {t('count.members', { count: fields.length })}
          </p>
          <div className="flex h-5 items-center">
            {saveState === 'saving' && (
              <span className="flex items-center gap-1.5 text-muted-foreground text-xs">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('reports.saving')}{' '}
              </span>
            )}
            {saveState === 'saved' && (
              <span className="flex items-center gap-1.5 text-green-600 text-xs">
                <Check className="h-3.5 w-3.5" /> {t('reports.saved')}{' '}
              </span>
            )}
          </div>
        </div>

        <div className="flex min-h-[min(70vh,720px)] flex-col gap-6 lg:flex-row">
          {/* ── Member list ── */}
          <div className="flex w-full shrink-0 flex-col gap-3 lg:w-56 lg:border-e lg:pe-4">
            <Button
              className="w-full justify-center"
              onClick={() => {
                const nextIndex = fields.length;
                append(EMPTY_MEMBER);
                setSelectedIndex(nextIndex);
              }}
              type="button"
              variant="outline"
            >
              <Plus className="me-2 h-4 w-4" />
              {t('reports.addKeyMember')}{' '}
            </Button>

            <div className="flex max-h-[480px] flex-col gap-1.5 overflow-y-auto pe-0.5">
              {fields.map((field, index) => {
                const m = watchedMembers?.[index];
                const fullName = getFullName(m?.firstName, m?.lastName, index);
                const active = index === selectedIndex;
                return (
                  <div className="flex items-center justify-between gap-2" key={field.id}>
                    <Button
                      className={cn(
                        'flex min-w-[80%] max-w-[80%] items-center justify-start gap-2 rounded-lg border px-3 py-5 text-start text-sm transition-colors',
                        active
                          ? 'border-primary bg-primary/5 font-medium text-primary'
                          : 'border-border bg-card text-muted-foreground hover:bg-muted/50',
                      )}
                      onClick={() => setSelectedIndex(index)}
                      type="button"
                    >
                      <User className="h-4 w-4 shrink-0 opacity-70" />
                      <span className="truncate">{fullName}</span>
                    </Button>
                    <Button
                      onClick={() => {
                        const rem = index;
                        remove(rem);
                        setSelectedIndex((prev) => {
                          if (prev > rem) return prev - 1;
                          if (prev === rem) return Math.max(0, rem - 1);
                          return prev;
                        });
                      }}
                      size="icon"
                      type="button"
                      variant="ghost"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Selected member form ── */}
          <div className="min-w-0 flex-1">
            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="font-semibold text-sm">
                  {getFullName(
                    watchedMembers?.[selectedIndex]?.firstName,
                    watchedMembers?.[selectedIndex]?.lastName,
                    selectedIndex,
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <MemberField
                    control={form.control}
                    index={selectedIndex}
                    label={t('reports.firstName2')}
                    name="firstName"
                  />
                  <MemberField
                    control={form.control}
                    index={selectedIndex}
                    label={t('reports.lastName2')}
                    name="lastName"
                  />
                  <MemberField
                    control={form.control}
                    index={selectedIndex}
                    label={t('reports.roleTitle')}
                    name="memberRole"
                  />
                  <MemberField
                    control={form.control}
                    index={selectedIndex}
                    label={t('reports.nationality')}
                    name="nationality"
                  />
                  <MemberField
                    control={form.control}
                    index={selectedIndex}
                    label={t('reports.dateOfBirth2')}
                    name="dateOfBirth"
                    placeholder={t('reports.yyyyMmDd')}
                  />
                  <MemberField
                    control={form.control}
                    index={selectedIndex}
                    label={t('reports.placeOfBirth2')}
                    name="placeOfBirth"
                  />
                  <MemberField
                    control={form.control}
                    index={selectedIndex}
                    label={t('reports.countryOfResidence')}
                    name="countryOfResidence"
                  />
                  <MemberField
                    control={form.control}
                    index={selectedIndex}
                    label={t('reports.idNumber2')}
                    name="idNumber"
                  />
                  <MemberField
                    control={form.control}
                    index={selectedIndex}
                    label={t('reports.taxIdNumber')}
                    name="taxIdentificationNumber"
                  />
                  <MemberField
                    control={form.control}
                    index={selectedIndex}
                    label={t('reports.phone')}
                    name="phoneNumber"
                    type="tel"
                  />
                  <MemberField
                    control={form.control}
                    index={selectedIndex}
                    label={t('reports.email')}
                    name="email"
                    type="email"
                  />
                  <MemberField
                    control={form.control}
                    index={selectedIndex}
                    label={t('reports.sourceOfFundsIncome')}
                    name="primarySourceOfFundsIncome"
                  />
                  <MemberField
                    control={form.control}
                    index={selectedIndex}
                    label={t('reports.estimatedNetWorth')}
                    name="estimatedNetWorth"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Separator />

        <div className="flex justify-end">
          <Button onClick={onContinue} type="button">
            {t('auth.continue')} <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" />
          </Button>
        </div>
      </form>
    </Form>
  );
}

function MemberField({
  control,
  index,
  name,
  label,
  placeholder,
  type = 'text',
}: {
  control: any;
  index: number;
  name: string;
  label: string;
  placeholder?: string;
  type?: string;
}) {
  useLocale();

  return (
    <FormField
      control={control}
      name={`keyMembers.${index}.${name}`}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-xs">{label}</FormLabel>
          <FormControl>
            {name === 'dateOfBirth' || name === 'incorporationDate' ? (
              <DateInput {...field} placeholder={label} />
            ) : (
              <TypewriterField
                className="h-8 text-sm"
                placeholder={placeholder ?? label}
                type={type}
                {...field}
              />
            )}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
